/**
 * Crossmint Signers Frame - Main Framework Entry Point
 */

import type {
	ExportSignerEventName,
	ExportSignerInputEvent,
	ExportSignerOutputEvent,
} from "@crossmint/client-signers";
import {
	encodeBytes,
	encodeEd25519SecretSeed,
} from "open-signer-shared-cryptography";
import {
	type CrossmintFrameServices,
	createCrossmintFrameServices,
	initializeHandlers,
} from "./services";
import {
	collectSafeDebugState,
	measureFunctionTime,
	withRetries,
} from "./services/common/utils";
import { createEnvironmentLogger } from "./services/logger";

// Define window augmentation
declare global {
	interface Window {
		CrossmintFrame: CrossmintFrame;
		CrossmintFrameReady: Promise<CrossmintFrame>;
		crossmintAppId?: string;
		ReactNativeWebView?: {
			postMessage: (message: string) => void;
		};
	}
}

export interface ExportHandler<
	EventName extends ExportSignerEventName = ExportSignerEventName,
> {
	event: `request:${EventName}`;
	responseEvent: `response:${EventName}`;
	callback: (
		payload: ExportSignerInputEvent<EventName>,
		services: CrossmintFrameServices,
		frame: CrossmintFrame,
	) => Promise<ExportSignerOutputEvent<EventName>>;
	options?: {
		timeoutMs: number;
	};
}

/**
 * Main Crossmint Signers Frame class
 */
class CrossmintFrame {
	private static instance: CrossmintFrame | null = null;
	private static initializationPromise: Promise<CrossmintFrame> | null = null;
	private readonly log_prefix = "[CrossmintFrame]";

	private constructor(
		private readonly logger = createEnvironmentLogger(),
		private readonly services = createCrossmintFrameServices(logger),
		private readonly handlers = initializeHandlers(services, logger),
	) {}

	/**
	 * Get the singleton instance of Crossmint Signers Frame
	 * @returns {Promise<CrossmintFrame>} The singleton instance
	 */
	static async getInstance(): Promise<CrossmintFrame> {
		if (CrossmintFrame.instance) {
			return CrossmintFrame.instance;
		}

		if (!CrossmintFrame.initializationPromise) {
			CrossmintFrame.initializationPromise = (async () => {
				const instance = new CrossmintFrame();
				CrossmintFrame.instance = instance;
				return instance;
			})();
		}

		return CrossmintFrame.initializationPromise;
	}

	/**
	 * Initialize the Crossmint Signers Frame framework
	 * Includes debug collection on failures for enhanced troubleshooting
	 */
	async init(): Promise<void> {
		// Step 1: Initialize all services (once)
		this.logger.info(
			this.log_prefix,
			"Initializing Crossmint Signers Frame framework...",
		);
		await this.initServices();

		// Step 2: Notify parent and handshake (retried on failure)
		this.logger.info(
			this.log_prefix,
			"Services initialized, notifying parent...",
		);
		await withRetries(() => this.services.events.notifyReady(), {
			retryDelays: [2000, 5000, 10000],
			onError: (error, attempt, maxAttempts) => {
				this.logger.error(
					this.log_prefix,
					`Handshake failed (attempt ${attempt}/${maxAttempts}):`,
					error instanceof Error ? error.name : "UnknownError",
				);
			},
			onRetry: (attempt, delayMs) => {
				this.logger.info(
					this.log_prefix,
					`Retrying handshake in ${delayMs}ms (attempt ${attempt + 1})...`,
				);
			},
		}).catch(async (error) => {
			await this.logSafeDebugState();
			throw error;
		});

		// Step 3: Register event handlers (once, after successful handshake)
		this.logger.info(this.log_prefix, "Registering event handlers...");
		await this.registerHandlers();
		this.logger.info(
			this.log_prefix,
			"Event handlers registered. Framework ready.",
		);
	}

	private async initServices(): Promise<void> {
		const initService = async (
			service: CrossmintFrameServices[keyof CrossmintFrameServices],
		) => {
			const serviceName = service.name;
			this.logger.info(service.log_prefix, `Initializing ${serviceName}...`);
			const { time } = await measureFunctionTime(() => service.init());
			this.logger.info(
				service.log_prefix,
				`${serviceName} initialized in ${time}ms`,
			);
		};

		try {
			await Promise.all(
				Object.values(this.services).map((service) => initService(service)),
			);
		} catch (error: unknown) {
			this.logger.error(
				this.log_prefix,
				"Service initialization failed:",
				error instanceof Error ? error.name : "UnknownError",
			);
			await this.logSafeDebugState();
			throw error;
		}
	}

	private async logSafeDebugState(): Promise<void> {
		try {
			const debugInfo = await collectSafeDebugState(this.services);
			this.logger.info(
				this.log_prefix,
				"Debug state:",
				JSON.stringify(debugInfo),
			);
		} catch {
			this.logger.error(this.log_prefix, "Failed to collect debug state");
		}
	}

	/**
	 * Initialize export handlers only
	 * Used by export.html to register only export-related events
	 */
	async initExportHandlers(exportHandlers: ExportHandler[]): Promise<void> {
		try {
			this.logger.debug(
				this.log_prefix,
				"Initializing Crossmint Signers Frame export handlers...",
			);

			await this.services.events.initExportMessenger();
			await this.services.attestation.init();
			await this.services.device.init();
			await this.services.secret.init();
			await this.services.cryptoKey.init();

			this.logger.debug(
				this.log_prefix,
				"-- Registering export event handlers",
			);
			await this.registerExportHandlers(exportHandlers);
			this.logger.debug(
				this.log_prefix,
				"-- Export event handlers properly registered",
			);
		} catch (error: unknown) {
			await this.logSafeDebugState();
			throw error;
		}
	}

	/**
	 * Formats a private key for export based on the scheme and encoding
	 */
	formatPrivateKeyForExport(
		privateKey: Uint8Array,
		scheme: "ed25519" | "secp256k1",
		encoding: "base58" | "hex" | "strkey",
	): string {
		try {
			switch (scheme) {
				case "ed25519":
					if (encoding === "strkey") {
						return encodeEd25519SecretSeed(privateKey);
					}
					return encodeBytes(privateKey, encoding);
				case "secp256k1":
					return `0x${encodeBytes(privateKey, "hex")}`;
				default:
					throw new Error(`Unsupported scheme: ${scheme}`);
			}
		} catch (error) {
			throw new Error(
				`Failed to format ${scheme} key: ${error instanceof Error ? error.message : String(error)}`,
			);
		}
	}

	private registerHandlers() {
		const messenger = this.services.events.getMessenger();
		for (const handler of this.handlers) {
			this.logger.debug(
				this.log_prefix,
				`   -- Registering handler for event ${handler.event}`,
			);
			messenger.on(handler.event, async (payload) => {
				const response = await handler.callback(payload);
				messenger.send(handler.responseEvent, response);
			});
			this.logger.debug(
				this.log_prefix,
				`  -- Handler for event ${handler.event} successfully registered`,
			);
		}
	}

	private registerExportHandlers(exportHandlers: ExportHandler[]) {
		const messenger = this.services.events.getExportMessenger();
		for (const handler of exportHandlers) {
			this.logger.debug(
				this.log_prefix,
				`   -- Registering export handler for event ${handler.event}`,
			);
			messenger.on(handler.event, async (payload) => {
				const response = await handler.callback(payload, this.services, this);
				messenger.send(handler.responseEvent, response);
			});
			this.logger.debug(
				this.log_prefix,
				`  -- Export handler for event ${handler.event} successfully registered`,
			);
		}
	}
}

// Initialize when loaded as ESM module in browser
if (typeof window !== "undefined") {
	window.CrossmintFrameReady = CrossmintFrame.getInstance().then(
		(crossmintFrameInstance) => {
			window.CrossmintFrame = crossmintFrameInstance;
			return crossmintFrameInstance;
		},
	);
}

// Export the CrossmintFrame class for direct usage
export default CrossmintFrame;
